// A person's own chat settings on the web (M63, docs/chat-round-five.md):
// archive (D231), the unread mark (D232) and mute durations (D233). Pure
// helpers, so the chat list and the page agree and tests can pin them down.
import { liveMutes, sortHome, type ChatSettings } from '@twinden/shared';
import type { PushPreferences } from './api';
import type { ChatConversation } from '../components/chat/ConversationDialogs';

/**
 * The home's order (D237): "Everyone", my pinned chats in the order I
 * pinned them, then the rest by their newest message.
 */
export function homeOrder<T extends ChatConversation>(list: T[]): T[] {
  return sortHome(list);
}

/** What a chat's badge counts: its real unread, or 1 when marked (D232). */
export function chatUnread(
  conversation: Pick<ChatConversation, 'unreadCount' | 'markedUnread'>
): number {
  return conversation.unreadCount || (conversation.markedUnread ? 1 : 0);
}

/**
 * A chat I keep archived (D231 with Keep archived, and archived now): quiet
 * like a muted one and out of the badge totals (D306).
 */
export function isKeptArchived(
  conversation: Pick<ChatConversation, 'archivedAt' | 'keepArchived'>
): boolean {
  return Boolean(conversation.archivedAt && conversation.keepArchived);
}

/**
 * Whether a chat in the cached home lists (any family) is kept archived:
 * the in-app sound stays quiet for it as for a muted chat (D306).
 */
export function keptArchivedIn(
  listsByFamily: Record<string, ChatConversation[]>,
  conversationId: string
): boolean {
  for (const list of Object.values(listsByFamily)) {
    const found = list.find((chat) => chat.id === conversationId);
    if (found) return isKeptArchived(found);
  }
  return false;
}

/**
 * What a chat adds to the app badge: its unread (D232), but nothing for a
 * chat kept archived, whose count shows only in the Archived view (D306).
 */
export function badgeUnread(
  conversation: Pick<
    ChatConversation,
    'unreadCount' | 'markedUnread' | 'archivedAt' | 'keepArchived'
  >
): number {
  return isKeptArchived(conversation) ? 0 : chatUnread(conversation);
}

export function hasUnread(
  conversation: Pick<ChatConversation, 'unreadCount' | 'markedUnread'>
): boolean {
  return chatUnread(conversation) > 0;
}

/**
 * The home's two lists (D231): chats in the main grid and archived ones,
 * each in the order given. "Everyone" is never archived.
 */
export function splitArchived<T extends ChatConversation>(
  conversations: T[]
): { main: T[]; archived: T[] } {
  const main: T[] = [];
  const archived: T[] = [];
  for (const conversation of conversations) {
    if (conversation.archivedAt && !conversation.isDefault) {
      archived.push(conversation);
    } else {
      main.push(conversation);
    }
  }
  return { main, archived };
}

/** A chat's list entry with new settings from the server applied. */
export function withSettings<T extends ChatConversation>(
  conversations: T[],
  settings: ChatSettings
): T[] {
  let changed = false;
  const next = conversations.map((conversation) => {
    if (conversation.id !== settings.conversationId) return conversation;
    changed = true;
    return {
      ...conversation,
      archivedAt: settings.archivedAt,
      keepArchived: settings.keepArchived,
      markedUnread: settings.markedUnread,
      // M64 (D237); older servers don't send it.
      pinnedAt: settings.pinnedAt ?? null,
    };
  });
  return changed ? next : conversations;
}

/** The muted list and end times with one chat's mute state applied (D233). */
export function withMute(
  prefs: PushPreferences,
  settings: Pick<ChatSettings, 'conversationId' | 'muted' | 'mutedUntil'>
): PushPreferences {
  const id = settings.conversationId;
  const live = liveMutes(prefs);
  const ids = live.mutedConversationIds.filter((other) => other !== id);
  const until = { ...live.mutedConversationUntil };
  delete until[id];
  if (settings.muted) {
    ids.push(id);
    if (settings.mutedUntil) until[id] = settings.mutedUntil;
  }
  return {
    ...prefs,
    mutedConversationIds: ids,
    mutedConversationUntil: until,
  };
}
